/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2022, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'./CrosstabGrid',
	'../common/PrintHelper',
	'../common/component/PrintTableGridComponent',
	'@waca/dashboard-common/js/utils/CommonUtil'
], (
	React,
	CrosstabGrid,
	PrintHelper,
	PrintTableGridComponent,
	CommonUtil
) => {

	/**
	* This class is responsible for rendering the entire crosstab data for pdf printing
	 */
	return class CrosstabPrintGrid extends CrosstabGrid {
		constructor(args) {
			super(args);
			this._coveredCornerCellSet = new Set();
		}

		_initializeGrid() {
			this.Grid = PrintTableGridComponent;
			this._gridDnDManager = null;
		}

		_getCrosstabGridComponentToMount (gridProperties = {}) {
			gridProperties.renderCell = this._renderCell.bind(this);
			gridProperties.getTableRows = this._getTableRows.bind(this);
			this.numRows = gridProperties.numRows;
			this.numColumns = gridProperties.numColumns;
			this.fixedRows = gridProperties.fixedRows;
			this.fixedColumns = gridProperties.fixedColumns;
			this.columnWidth = gridProperties.columnWidth;
			const grid = React.createElement(this.Grid, gridProperties);
			return React.createElement('div', { class: 'xtab-grid-view' }, [ grid ]);
		}

		_fixedCornerCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, _dataProvider) {
			if(this._coveredCornerCellSet.has(`${rowIndex} ${colIndex}`)) {
				return null;
			}
			const content = super._fixedCornerCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, _dataProvider);
			const spanInfo = gridCellProps.spanInfo;
			this._updateCoveredCornerCellSet(spanInfo);
			return content;
		}

		_updateCoveredCornerCellSet(spanInfo){
			for (let row = 0; row <= spanInfo.rowSpan; row++) {
				for (let col = 0; col <= spanInfo.colSpan; col++) {
					if (row !== 0 || col !== 0) {
						this._coveredCornerCellSet.add(`${row} ${col}`);
					}
				}
			}
		}

		_preRenderReactGrid() {
			super._preRenderReactGrid();
			this.$el.addClass('xtab-print-grid');
			PrintHelper.setContainerElementStyles(this.$el[0]);
		}

		_dataCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, numColumns, _dataProvider) {
			const shapeCol = colIndex + fixedColumns;
			const previousShapeCol = this._shapeCols.includes(shapeCol);
			const content = super._dataCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, numColumns, _dataProvider);
			// this._shapeCols get calculated during parent._dataCellRenderer
			// The very first time parent._dataCellRenderer the columnWidth may not have conditional styles set (i.e. this._shapeCols calculated)			// Recheck again after the data cell rendered and set column width appropriate to make room for the conditional shape icon
			if (!previousShapeCol && this._shapeCols.includes(shapeCol)) {
				const style = gridCellProps.style;
				style.width = this.columnWidth(shapeCol);
			}
			return content;
		}

		addSpanInfo(spanInfo = {}, gridCellProps = {}) {
			gridCellProps.spanInfo = {
				...spanInfo
			};
		}

		canResizeToWidget() {
			// return false to not resize the table after finished rendering the table
			return false;
		}

		_getTableRows ({ renderer }) {
			if (!this._rowsGroupedByRowSpans) {
				this._rowsGroupedByRowSpans = PrintHelper.getTableRows({
					numRows: this.numRows,
					numColumns: this.numColumns,
					fixedRows: this.fixedRows,
					fixedColumns: this.fixedColumns,
					renderer,
					type: this.content.getFeature('Visualization').getType()
				});
			}
			return this._rowsGroupedByRowSpans;
		}

		/*
		 * Massages input and output to generic render function
		 */
		_renderCell({
			renderer,
			rowIndex,
			colIndex,
			props = {}
		}) {
			const attributes = {
				className: '',
				isExpansionLineageHintSupported: false,
				...props
			};

			const content = renderer(rowIndex, colIndex, attributes);
			if (content) {
				const spanInfo = attributes.spanInfo || {};
				if (spanInfo.rowSpan) {
					attributes.rowSpan = spanInfo.rowSpan + 1;
				}
				if (spanInfo.colSpan) {
					attributes.colSpan = spanInfo.colSpan + 1;
				}
			}
			delete attributes.spanInfo;
			// Most CSS styles should be shared between the <td> and its contained <div> - but
			// styles which compound one another need to be removed.
			const tdAttributes = CommonUtil.deepClone(attributes);
			delete tdAttributes.style.paddingLeft;
			delete tdAttributes.style.paddingTop;
			delete tdAttributes.style.display;
			return {
				content,
				attributes: tdAttributes
			};
		}
	};
});
